import { createExternalValue } from "@nova/toolkit-pure-hooks"
import { WeatherMode } from "features/map/control/types"
import { MAX_FRAME_BYTES, type WeatherFrame } from "./weatherTileSource"

const HOUR_MS = 60 * 60 * 1000
const SWEEP_FRAME_BUDGET = 96
const MAX_CACHE_BYTES = SWEEP_FRAME_BUDGET * MAX_FRAME_BYTES
const MAX_CACHE_ENTRIES = 1024
const REFRESH_FRONTIER_MS = HOUR_MS
const FRAME_TTL_MS = 5 * 60 * 1000
const FRAME_RETRY_BACKOFF_MS = 30 * 1000

const frameCache = new Map<string, WeatherFrame | null>()
const frameFetchedAt = new Map<string, number>()
const frameAccessedAt = new Map<string, number>()
const frameVersion = new Map<string, string>()
const frameFailedAt = new Map<string, number>()
const inFlightFrames = new Map<string, Promise<void>>()
const inFlightControllers = new Map<string, AbortController>()

let cachedBytes = 0

const pinnedByMode = new Map<WeatherMode, Set<WeatherFrame>>()
const pendingClose = new Set<WeatherFrame>()

const isPinned = (frame: WeatherFrame) => {
    for (const pinned of pinnedByMode.values()) if (pinned.has(frame)) return true
    return false
}

export const setPinnedFrames = (mode: WeatherMode, frames: Iterable<WeatherFrame>) => {
    const pinned = new Set(frames)
    if (pinned.size === 0) pinnedByMode.delete(mode)
    else pinnedByMode.set(mode, pinned)
    for (const frame of pendingClose) {
        if (isPinned(frame)) continue
        frame.image.bitmap.close()
        pendingClose.delete(frame)
    }
}

const frameBytes = (frame: WeatherFrame | null | undefined) =>
    frame ? frame.image.width * frame.image.height * 4 : 0

export const frameKey = (mode: WeatherMode, bk: string, hourMs: number) => `${mode}|${bk}|${hourMs}`

const isLeadingEdgeFrame = (hourMs: number, nowMs: number) => hourMs >= nowMs - REFRESH_FRONTIER_MS

const isStaleFrame = (key: string, nowMs: number) =>
    nowMs - (frameFetchedAt.get(key) ?? 0) > FRAME_TTL_MS

const cacheGeneration = createExternalValue(0)
export const useCacheGeneration = cacheGeneration.use

const bumpCache = () => cacheGeneration.update((generation) => generation + 1)

const dropFrame = (key: string) => {
    const frame = frameCache.get(key)
    cachedBytes -= frameBytes(frame)
    if (frame) {
        if (isPinned(frame)) pendingClose.add(frame)
        else frame.image.bitmap.close()
    }
    frameCache.delete(key)
    frameFetchedAt.delete(key)
    frameAccessedAt.delete(key)
    frameFailedAt.delete(key)
    frameVersion.delete(key)
}

const isOverCap = () => cachedBytes > MAX_CACHE_BYTES || frameCache.size > MAX_CACHE_ENTRIES

const evictionOrder = (justSet: string) =>
    [...frameCache.entries()]
        .filter(([key]) => key !== justSet)
        .map(([key, frame]) => ({
            key,
            pinned: frame !== null && isPinned(frame),
            accessedAt: frameAccessedAt.get(key) ?? 0,
        }))
        .sort((a, b) => Number(a.pinned) - Number(b.pinned) || a.accessedAt - b.accessedAt)

const evictToCap = (justSet: string) => {
    if (!isOverCap()) return
    for (const { key } of evictionOrder(justSet)) {
        dropFrame(key)
        if (!isOverCap()) return
    }
}

export const cacheSet = (key: string, frame: WeatherFrame | null, version: string | null) => {
    if (frameCache.has(key)) dropFrame(key)
    frameCache.set(key, frame)
    cachedBytes += frameBytes(frame)
    frameFetchedAt.set(key, Date.now())
    frameAccessedAt.set(key, Date.now())
    frameFailedAt.delete(key)
    if (version === null) frameVersion.delete(key)
    else frameVersion.set(key, version)
    evictToCap(key)
    bumpCache()
}

export const dropFrames = (prefix: string) => {
    const stale = [...frameCache.keys()].filter((key) => key.startsWith(prefix))
    if (stale.length === 0) return
    stale.forEach(dropFrame)
    bumpCache()
}

export const cacheFrameBudget = (frameBytes: number) =>
    Math.min(MAX_CACHE_ENTRIES, Math.max(1, Math.floor(MAX_CACHE_BYTES / frameBytes)))

export const getCachedFrame = (key: string) => {
    const frame = frameCache.get(key)
    if (frame !== undefined) frameAccessedAt.set(key, Date.now())
    return frame
}

export const needsFetch = (
    mode: WeatherMode,
    bk: string,
    hourMs: number,
    nowMs: number,
    version: string | null
) => {
    const key = frameKey(mode, bk, hourMs)
    if (inFlightFrames.has(key)) return false
    const failedAt = frameFailedAt.get(key)
    if (failedAt !== undefined && nowMs - failedAt < FRAME_RETRY_BACKOFF_MS) return false
    if (!frameCache.has(key)) return true
    if (version !== null && frameVersion.get(key) !== version) return true
    return isLeadingEdgeFrame(hourMs, nowMs) && isStaleFrame(key, nowMs)
}

// The fetch pump only re-runs when the cache generation moves, so the backoff has to supply its
// own clock: a burst whose fetches all reject produces no other event to retry against.
export const markFrameFailed = (key: string) => {
    frameFailedAt.set(key, Date.now())
    setTimeout(() => {
        if (frameFailedAt.delete(key)) bumpCache()
    }, FRAME_RETRY_BACKOFF_MS)
}

export const registerInFlightFrame = (
    key: string,
    promise: Promise<void>,
    controller: AbortController
) => {
    inFlightFrames.set(key, promise)
    inFlightControllers.set(key, controller)
}

export const clearInFlightFrame = (key: string) => {
    inFlightFrames.delete(key)
    inFlightControllers.delete(key)
}

export const abortInFlightFrames = (prefix: string, shouldAbort?: (hourMs: number) => boolean) => {
    for (const [key, controller] of inFlightControllers) {
        if (!key.startsWith(prefix)) continue
        if (!shouldAbort) {
            controller.abort()
            continue
        }
        const hourMs = Number(key.slice(prefix.length))
        if (shouldAbort(hourMs)) controller.abort()
    }
}
